// src/components/learning/PreLesson.tsx
import React, { useState } from "react";
import styled from "styled-components";

// -------------------------------
// Styled Components
// -------------------------------
const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  font-family: "Roboto", sans-serif;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  margin-bottom: 15px;
  font-size: 1.8rem;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 Ratio */
  margin-bottom: 20px;
`;

const VideoFrame = styled.iframe`
  position: absolute;
  width: 100%;
  height: 100%;
  border: none;
`;

const GoalsList = styled.ul`
  list-style-type: disc;
  margin-left: 20px;
  font-size: 1.1rem;
`;

const Form = styled.form`
  margin-top: 10px;
`;

const Input = styled.input`
  padding: 10px;
  font-size: 1rem;
  width: 80%;
  margin-right: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.primary || "#7e22ce"};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${({ theme }) => theme.primaryDark || "#5a17a7"};
  }
`;

const InteractiveImage = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  height: 400px;
  margin: 20px 0;
  background: url('/path/to/human-body-diagram.jpg') center center/cover no-repeat;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
`;

const Overlay = styled.div<{ hovered: boolean }>`
  position: absolute;
  top: 35%;
  left: 42%;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 10px 15px;
  border-radius: 6px;
  opacity: ${({ hovered }) => (hovered ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const Badge = styled.div`
  display: inline-block;
  background: gold;
  color: #555;
  padding: 5px 12px;
  border-radius: 20px;
  margin-top: 15px;
  font-weight: bold;
  font-size: 1rem;
`;

const ContinueButton = styled(Button)`
  margin-top: 30px;
`;

// -------------------------------
// PreLessonProps Interface
// -------------------------------
interface PreLessonProps {
  onBegin: () => void;
}

// -------------------------------
// PreLesson Component
// -------------------------------
const PreLesson: React.FC<PreLessonProps> = ({ onBegin }) => {
  // States for interactive sections.
  const [hovered, setHovered] = useState(false);
  const [priorKnowledge, setPriorKnowledge] = useState("");
  const [goal, setGoal] = useState("");
  const [badgeEarned, setBadgeEarned] = useState(false);

  // Handler for the Prior Knowledge form.
  const handleKnowledgeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // In a real scenario you might save or send this data for further processing.
    alert(`Thanks for sharing: ${priorKnowledge}`);
  };

  // Handler for setting the personal goal.
  const handleSetGoal = () => {
    if (goal.trim()) {
      setBadgeEarned(true);
      alert(`Goal set: ${goal}`);
    }
  };

  return (
    <Container>
      {/* Lesson Preview & Objectives */}
      <Section>
        <SectionTitle>Introduction Video: What is the Heart?</SectionTitle>
        <VideoContainer>
          <VideoFrame
            title="Introduction to the Heart"
            src="https://www.youtube.com/embed/dummy-video" // Replace with your actual video URL
            allowFullScreen
          />
        </VideoContainer>
        <SectionTitle>Learning Goals</SectionTitle>
        <GoalsList>
          <li>Understand the basic anatomy of the heart</li>
          <li>Learn how blood circulates through the body</li>
          <li>Recognize why maintaining heart health is essential</li>
        </GoalsList>
      </Section>

      {/* Prior Knowledge Assessment */}
      <Section>
        <SectionTitle>Prior Knowledge Assessment</SectionTitle>
        <p>What do you already know about the heart?</p>
        <Form onSubmit={handleKnowledgeSubmit}>
          <Input
            type="text"
            value={priorKnowledge}
            onChange={(e) => setPriorKnowledge(e.target.value)}
            placeholder="Type your answer here..."
          />
          <Button type="submit">Submit Answer</Button>
        </Form>
      </Section>

      {/* Interactive Overview */}
      <Section>
        <SectionTitle>Interactive Virtual Tour</SectionTitle>
        <p>Hover over the image to see where the heart is located on the human body.</p>
        <InteractiveImage
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <Overlay hovered={hovered}>
            This is your chest—the heart is right here!
          </Overlay>
        </InteractiveImage>
        <SectionTitle>Historical Context & Fun Fact</SectionTitle>
        <p>
          Did you know? Ancient Egyptians believed the heart was the center of
          life and emotion. Today, we have a detailed understanding of its
          anatomy and function!
        </p>
      </Section>

      {/* Motivational Prompt */}
      <Section>
        <SectionTitle>Set Your Learning Goal</SectionTitle>
        <p>Before we begin, what do you hope to achieve in this lesson? Set a personal goal!</p>
        <Input
          type="text"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="Enter your goal..."
        />
        <Button onClick={handleSetGoal}>Set Goal</Button>
        {badgeEarned && <Badge>Badge Earned: Heart Explorer</Badge>}
      </Section>

      {/* Continue Button */}
      <ContinueButton onClick={onBegin}>Continue to Lesson</ContinueButton>
    </Container>
  );
};

export default PreLesson;
